/**
 * voice-v8 — Was Erkennung und Sprachausgabe kosten. EINE Stelle. (23.09.2026)
 *
 * Ticket 2529 (Martin, 23.09.2026): *„Lückenlos messen. stt_usd und tts_usd
 * stehen in usage_events über ALLE Sitzungen auf 0,00 — keine Stelle
 * schreibt sie."*
 *
 * WARUM NEBEN `modell-preise.ts` UND NICHT DARIN
 *
 *   `modell-preise.ts` rechnet Token. Erkennung rechnet in Audiostunden,
 *   ElevenLabs in Zeichen, Soniox-Sprachausgabe in Text- UND Audio-Token, die
 *   wir aus Zeichen und Sekunden erst schätzen müssen. Das sind andere
 *   Einheiten — aber sie gehören genauso an EINE Stelle. Vorher standen zwei
 *   dieser Sätze in `agent/cost.ts` (an LiveKit gebunden, ElevenLabs mit dem
 *   Creator-Abo 0,18 $ statt dem API-Preis), und alle anderen Wege hatten
 *   gar keinen.
 *
 * ⚠️ Umrechnungen (Sekunden aus MP3-Bytes, Token aus Zeichen) stehen NUR hier.
 *    Wer sie woanders nachbaut, hat am Tag der ersten Preisänderung zwei
 *    Wahrheiten.
 *
 * DIE REGEL AUS modell-preise.ts GILT AUCH HIER
 *
 *   Null ist eine Behauptung, kein Platzhalter. Ein unbekannter Dienst wird
 *   mit dem teuersten BELEGTEN Satz seiner Art gerechnet und als `geschaetzt`
 *   markiert — nie mit 0,00 $.
 *
 * QUELLEN (alle abgerufen am 23.09.2026)
 *
 *   Soniox       https://soniox.com/pricing
 *   Groq         https://console.groq.com/docs/model/whisper-large-v3-turbo
 *                https://console.groq.com/docs/model/whisper-large-v3
 *                https://console.groq.com/docs/speech-to-text (Mindestlänge)
 *   ElevenLabs   https://elevenlabs.io/pricing/api (auch Musik)
 *   Google       https://ai.google.dev/gemini-api/docs/pricing (Bilder, Lyria)
 */

/**
 * `musik` und `bild` kamen am 23.09.2026 dazu (Ticket 2529, Prüfung). Für
 * sie hat usage_events keine eigene Spalte — sie stehen nur in total_usd und
 * sind am Kanal zu erkennen (`musik-…`, `bild-…`).
 */
export type DienstArt = "stt" | "tts" | "musik" | "bild";

/** Erkennung: abgerechnet nach Audiodauer. */
interface ErkennungsPreis {
  /** Dollar je Stunde Audio. */
  usdJeStunde: number;
  /** Kürzere Anfragen werden trotzdem so lang berechnet (Groq: 10 s). */
  mindestSekundenJeAnfrage: number;
  quelle: string;
}

/**
 * Schlüssel `anbieter:modell` in Kleinschreibung. Längere Schlüssel
 * gewinnen beim Präfix (siehe `erkennungsPreisFuer`).
 */
export const ERKENNUNGS_PREISE: Record<string, ErkennungsPreis> = {
  /*
   * Groq, 23.09.2026: „whisper-large-v3-turbo $0.04 per hour" und
   * „Minimum Billed Length: 10 seconds. If you submit a request less than
   * this, you will still be billed for 10 seconds."
   *
   * ⚠️ Die Mindestlänge trifft die Sprechtaste: ein „Ja." von einer Sekunde
   *    kostet zehn.
   */
  "groq:whisper-large-v3-turbo": {
    usdJeStunde: 0.04,
    mindestSekundenJeAnfrage: 10,
    quelle: "console.groq.com/docs/model/whisper-large-v3-turbo, 23.09.2026",
  },
  /*
   * ⚠️ NICHT turbo: Tagesaufnahme, Datei-Transkript und der Groq-Rückfall
   *    des Sprecher-Laufs nehmen seit 02.09. large-v3 (transcribe-file.ts).
   *    Groq, 23.09.2026: „$0.111" je Stunde — fast das Dreifache von turbo.
   *    Die Zehn-Sekunden-Grenze steht auf der allgemeinen Seite und gilt
   *    für jede Anfrage, nicht je Modell.
   */
  "groq:whisper-large-v3": {
    usdJeStunde: 0.111,
    mindestSekundenJeAnfrage: 10,
    quelle: "console.groq.com/docs/model/whisper-large-v3, 23.09.2026",
  },
  /*
   * Soniox, 23.09.2026: Datei-Erkennung „≈ $0.10/hour", Echtzeit
   * „≈ $0.12/hour". Abgerechnet wird in Token (Audio ein, Text aus); die
   * Stundensätze sind Sonioxʼ eigene Umrechnung.
   *
   * ⚠️ Nicht enthalten: Kontext-Text, den die Echtzeit-Sitzung mitbekommt
   *    (4,00 $ je 1 Mio. Text-Token). Siehe offene Lücken im Ticket.
   *
   * 04.11.2026 (STT v6, shared/soniox-modelle.ts): soniox.com/pricing erneut
   * gelesen — Datei „$0.10/hour", Echtzeit „$0.12/hour" (der Rechner nennt
   * dort `stt-rt-v6`), unverändert. Die Schlüssel haben bewusst KEINE
   * Versionsnummer: v6 und die v5-Zeilen, die schon in der Ablage stehen,
   * fallen auf denselben Satz. Eine Zeile je Version würde alte Buchungen
   * ins Unbekannte (Vorsichtssatz, `geschaetzt`) schieben.
   * ⚠️ Für Async nennt die Seite den Satz nur als Art („Async (file)"), nicht
   *    ausdrücklich für `stt-async-v6` — Annahme: gleicher Satz wie v5.
   */
  "soniox:stt-async": {
    usdJeStunde: 0.1,
    mindestSekundenJeAnfrage: 0,
    quelle: "soniox.com/pricing, 04.11.2026",
  },
  "soniox:stt-rt": {
    usdJeStunde: 0.12,
    mindestSekundenJeAnfrage: 0,
    quelle: "soniox.com/pricing, 04.11.2026",
  },
};

/**
 * Sprachausgabe nach Zeichen (ElevenLabs).
 *
 * ElevenLabs, 23.09.2026: „v3 $0.10", „v2 Multilingual $0.10",
 * „Flash / Turbo $0.05" je 1.000 Zeichen.
 *
 * 07.10.2026 (Eleven v4), elevenlabs.io/pricing/api: „v4 … $0.08 Price per
 * 1K characters", „v4 Turbo … $0.04" — gebucht wird der Listenpreis, nicht
 * der Aktionspreis („72% off until Oct 12": $0.022 / $0.011); lieber zu hoch
 * als eine Zahl, die am 13.10. still falsch ist.
 *
 * ⚠️ Beide Schlüssel ausdrücklich: `nachPraefix` gäbe `eleven_v4_turbo` sonst
 *    den Satz von `eleven_v4`.
 * ⚠️ Dieselbe Seite nennt am 07.10. für v3 und v2 Multilingual $0.08, für
 *    Flash/Turbo $0.04 — die Sätze oben stehen noch auf dem 23.09. (offen,
 *    nicht Teil der v4-Einbindung).
 * 21.10.2026 (v4 als Vorgabe): Preisseite erneut gelesen — v4 $0.08, v4 Turbo
 *    $0.04 je 1.000 Zeichen, unverändert; die Aktion ist vorbei. Die Seite
 *    nennt keine Modellkennungen, nur Namen; die Zuordnung „v4" → `eleven_v4`
 *    ist unsere. v3 ebenso neu belegt: „v3 … $0.08" je 1.000 Zeichen
 *    (elevenlabs.io/pricing/api, abgerufen 21.10.2026) — statt $0.10 vom 23.09.
 */
export const ZEICHEN_PREISE: Record<string, number> = {
  "elevenlabs:eleven_v4": 0.08,
  "elevenlabs:eleven_v4_turbo": 0.04,
  "elevenlabs:eleven_v3": 0.08, // elevenlabs.io/pricing/api, 21.10.2026
  "elevenlabs:eleven_multilingual_v2": 0.1,
  "elevenlabs:eleven_flash_v2_5": 0.05,
  "elevenlabs:eleven_turbo_v2_5": 0.05,
};

/**
 * Soniox-Sprachausgabe (tts-rt). Soniox, 23.09.2026: Eingabe-Text 4,00 $,
 * Ausgabe-Audio 21,50 $ je 1 Mio. Token; „1 character ≈ 0.3 input text
 * tokens", „1 hour of generated speech ≈ 30,000 output audio tokens",
 * zusammen „≈ $0.70/hour of generated speech".
 */
export const SONIOX_TTS = {
  usdJeMioTextToken: 4.0,
  usdJeMioAudioToken: 21.5,
  textTokenJeZeichen: 0.3,
  audioTokenJeStunde: 30_000,
  quelle: "soniox.com/pricing, 23.09.2026",
} as const;

/**
 * Vorsichtssätze — der TEUERSTE belegte Satz der jeweiligen Art.
 *
 * ⚠️ Gilt für Google Chirp3-HD, Google klassisch, Gemini-TTS und OpenAI-TTS:
 *    für sie gibt es in der belegten Liste keinen Preis. Lieber zu hoch mit
 *    Fahne als eine Null, die niemand bemerkt (siehe modell-preise.ts).
 */
export const ERKENNUNG_VORSICHTSSATZ_JE_STUNDE = Math.max(
  ...Object.values(ERKENNUNGS_PREISE).map((p) => p.usdJeStunde),
);
export const SPRACHAUSGABE_VORSICHTSSATZ_JE_TAUSEND_ZEICHEN = Math.max(
  ...Object.values(ZEICHEN_PREISE),
);

function schluessel(anbieter: string, modell: string): string {
  return `${anbieter.toLowerCase()}:${modell.toLowerCase()}`;
}

/** Längster Präfix gewinnt — wie `preisFuer` in modell-preise.ts. */
function nachPraefix<T>(tabelle: Record<string, T>, key: string): T | undefined {
  const direkt = tabelle[key];
  if (direkt !== undefined) return direkt;
  let treffer: T | undefined;
  let laenge = -1;
  for (const [k, p] of Object.entries(tabelle)) {
    if (key.startsWith(k) && k.length > laenge) {
      treffer = p;
      laenge = k.length;
    }
  }
  return treffer;
}

export function erkennungsPreisFuer(
  anbieter: string,
  modell: string,
): ErkennungsPreis | undefined {
  /*
   * ⚠️ „whisper-large-v3-turbo" beginnt mit „whisper-large-v3". Ohne den
   *    Vorrang des längeren Schlüssels bekäme turbo den fast dreifachen
   *    Satz — oder, andersherum eingetragen, large-v3 den billigen.
   */
  return nachPraefix(ERKENNUNGS_PREISE, schluessel(anbieter, modell));
}

interface DienstKosten {
  usd: number;
  /** Wahr, wenn Menge ODER Preis geschätzt ist. */
  geschaetzt: boolean;
  /** Was abgerechnet wurde, in der Einheit des Dienstes (z. B. Sekunden). */
  abgerechnet: number;
}

/**
 * Was eine Erkennung kostet.
 *
 * `sekunden` ist die Dauer des GESENDETEN Tons (nach Stille-Entfernung),
 * nicht die der Originalaufnahme. `null` heißt: nicht feststellbar — dann
 * zählt nur die Mindestlänge, und die Zahl ist geschätzt.
 *
 * Wirft nie.
 */
export function kostenErkennung(v: {
  anbieter: string;
  modell: string;
  sekunden: number | null | undefined;
  /** Menge oder Quelle ist geschätzt (z. B. Echtzeit-Dauer aus dem Upload). */
  geschaetzt?: boolean;
}): DienstKosten {
  const preis = erkennungsPreisFuer(v.anbieter ?? "", v.modell ?? "");
  const roh = Number(v.sekunden);
  const bekannt = Number.isFinite(roh) && roh > 0;
  const mindest = preis?.mindestSekundenJeAnfrage ?? 0;
  const abgerechnet = Math.max(bekannt ? roh : 0, mindest);
  const satz = preis?.usdJeStunde ?? ERKENNUNG_VORSICHTSSATZ_JE_STUNDE;
  return {
    usd: (abgerechnet / 3600) * satz,
    geschaetzt: Boolean(v.geschaetzt) || !preis || !bekannt,
    abgerechnet,
  };
}

/**
 * Sekunden aus MP3-Bytes bei fester Bitrate — GESCHÄTZT.
 *
 * ⚠️ Stimmt nur für CBR-Ton ohne großen Dateikopf. Soniox liefert, was wir
 *    anfragen (soniox-tts.ts: `bitrate`); ein paar hundert Byte ID3-Kopf
 *    machen bei einem Satz von 80 kB unter einem Prozent aus.
 */
export function mp3SekundenGeschaetzt(bytes: number, bitrateBps: number): number {
  const b = Number(bytes);
  const r = Number(bitrateBps);
  if (!(b > 0) || !(r > 0)) return 0;
  return (b * 8) / r;
}

/**
 * Was eine Sprachausgabe kostet.
 *
 * - Soniox: Text-Token aus Zeichen PLUS Audio-Token aus Sekunden — beides
 *   nach Sonioxʼ eigener Umrechnung, also immer `geschaetzt`.
 * - ElevenLabs: Zeichen × Satz des Modells.
 * - Alles andere: Zeichen × Vorsichtssatz, `geschaetzt`.
 *
 * Wirft nie.
 */
export function kostenSprachausgabe(v: {
  anbieter: string;
  modell?: string | null;
  zeichen: number;
  /** Nur Soniox: erzeugte Sekunden (siehe `mp3SekundenGeschaetzt`). */
  sekunden?: number | null;
}): DienstKosten {
  const zeichen = Math.max(0, Number(v.zeichen) || 0);
  const anbieter = (v.anbieter ?? "").toLowerCase();
  if (anbieter === "soniox") {
    const sek = Math.max(0, Number(v.sekunden) || 0);
    const textToken = zeichen * SONIOX_TTS.textTokenJeZeichen;
    const audioToken = (sek / 3600) * SONIOX_TTS.audioTokenJeStunde;
    return {
      usd:
        (textToken * SONIOX_TTS.usdJeMioTextToken) / 1_000_000 +
        (audioToken * SONIOX_TTS.usdJeMioAudioToken) / 1_000_000,
      geschaetzt: true,
      abgerechnet: sek,
    };
  }
  const satz = nachPraefix(ZEICHEN_PREISE, schluessel(anbieter, v.modell ?? ""));
  return {
    usd:
      (zeichen / 1000) *
      (satz ?? SPRACHAUSGABE_VORSICHTSSATZ_JE_TAUSEND_ZEICHEN),
    geschaetzt: satz === undefined,
    abgerechnet: zeichen,
  };
}

/*
 * ═══════════════════════════════════════════════════════════════════════
 * MUSIK UND BILDER (23.09.2026, Ticket 2529, Prüfung)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Die Prüfung fand sie ungebucht: den „Klang des Tages" im nächtlichen
 * Essenz-Lauf (ElevenLabs, Lyria als Ersatz und A/B-Zweitstück), das
 * Werkzeug generate_music und alle Bildwege (generate_image, edit_image,
 * Magazin-Titelbild in make_pdf, Musik-Cover). Martin: *„Hintergrundjobs je
 * Nutzer buchen … lückenlos."*
 */

/**
 * Musik — ElevenLabs je Minute, Lyria je Lied.
 *
 * ElevenLabs, https://elevenlabs.io/pricing/api, abgerufen 23.09.2026:
 * Music „$0.15" je Minute, „Music and Sound Effects are billed per
 * generation."
 * 21.10.2026 (Music v2.5, model_id music_v2_5): dieselbe Seite nennt weiter
 * EINEN Satz „Eleven Music $0.15 per minute" ohne Unterschied nach Modell
 * (v1, v2, v2.5). Darum gilt der Minutensatz für alle drei — ein eigener
 * v2.5-Preis ist nicht veröffentlicht und wird hier nicht erfunden.
 * Google, https://ai.google.dev/gemini-api/docs/pricing, abgerufen
 * 23.09.2026: „Lyria 3.5 (Full Song) $0.08 per song", „Lyria 3 Pro Preview
 * (Full Song) $0.08 per song", „Lyria 3 Clip Preview (30s) $0.04 per song".
 */
export const MUSIK_PREISE = {
  elevenlabsUsdJeMinute: 0.15,
  lyriaUsdJeLied: {
    "lyria-3.5": 0.08,
    "lyria-3-pro-preview": 0.08,
    "lyria-3-clip-preview": 0.04,
  } as Record<string, number>,
  quelle: "elevenlabs.io/pricing/api, ai.google.dev/gemini-api/docs/pricing, 23.09.2026",
} as const;

/**
 * Was ein Musikstück kostet.
 *
 * ⚠️ ElevenLabs: die Dauer ist die ANGEFORDERTE Länge (Kompositionsplan
 *    bzw. music_length_ms). Ob ElevenLabs sekundengenau oder je angefangene
 *    Minute abrechnet, steht nicht auf der Preisseite — deshalb `geschaetzt`.
 * ⚠️ Lyria: ein unbekanntes Modell (LYRIA_MODEL) kostet den teuersten
 *    belegten Liedpreis, mit Fahne.
 *
 * Wirft nie.
 */
export function kostenMusik(v: {
  anbieter: string;
  modell?: string | null;
  /** Nur ElevenLabs: angeforderte Länge in Sekunden. */
  sekunden?: number | null;
}): DienstKosten {
  const anbieter = (v.anbieter ?? "").toLowerCase();
  if (anbieter === "elevenlabs") {
    const sek = Math.max(0, Number(v.sekunden) || 0);
    return {
      usd: (sek / 60) * MUSIK_PREISE.elevenlabsUsdJeMinute,
      geschaetzt: true,
      abgerechnet: sek,
    };
  }
  const satz = MUSIK_PREISE.lyriaUsdJeLied[(v.modell ?? "").toLowerCase()];
  return {
    usd: satz ?? Math.max(...Object.values(MUSIK_PREISE.lyriaUsdJeLied)),
    geschaetzt: satz === undefined,
    abgerechnet: 1,
  };
}

/**
 * Bildmodelle — Dollar je 1 Mio. Token, getrennt nach Eingabe, Text-
 * Ausgabe (samt Denken) und Bild-Ausgabe.
 *
 * Google, https://ai.google.dev/gemini-api/docs/pricing, abgerufen
 * 23.09.2026, Stufe „Standard":
 *   gemini-3-pro-image      Eingabe 2,00 $ (Text/Bild), Ausgabe Text 12,00 $,
 *                           Ausgabe Bild 120,00 $ („$0.134 per 1K/2K image",
 *                           1.120 Token)
 *   gemini-3.1-flash-image  Eingabe 0,50 $, Ausgabe Text 3,00 $, Ausgabe Bild
 *                           60,00 $ („$0.067 per 1K image", 1.120 Token)
 *   gemini-2.5-flash-image  Eingabe 0,30 $, Ausgabe Text 2,50 $, Ausgabe Bild
 *                           „$0.039 per image" bei 1.290 Token
 *
 * ⚠️ Bei 2.5 Flash Image nennt die Seite nur den Preis je Bild; der Satz je
 *    Token ist daraus gerechnet (0,039 $ / 1.290 Token).
 */
interface BildPreis {
  usdJeMioEin: number;
  usdJeMioAusText: number;
  usdJeMioAusBild: number;
  /** Token je Bild bei der Vorgabe-Auflösung (1K) — nur für den Rückfall. */
  tokenJeBild: number;
}

export const BILD_PREISE: Record<string, BildPreis> = {
  "gemini-3-pro-image": {
    usdJeMioEin: 2,
    usdJeMioAusText: 12,
    usdJeMioAusBild: 120,
    tokenJeBild: 1120,
  },
  "gemini-3.1-flash-image": {
    usdJeMioEin: 0.5,
    usdJeMioAusText: 3,
    usdJeMioAusBild: 60,
    tokenJeBild: 1120,
  },
  "gemini-2.5-flash-image": {
    usdJeMioEin: 0.3,
    usdJeMioAusText: 2.5,
    usdJeMioAusBild: (0.039 / 1290) * 1_000_000,
    tokenJeBild: 1290,
  },
};

/** Wie Gemini `generateContent` den Verbrauch eines Bildaufrufs meldet. */
export interface BildNutzung {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  thoughtsTokenCount?: number;
  candidatesTokensDetails?: Array<{ modality?: string; tokenCount?: number }>;
}

/**
 * Was ein Bildaufruf kostet — aus `usageMetadata`, sonst je Bild.
 *
 * ⚠️ Ohne Aufteilung der Ausgabe (`candidatesTokensDetails`) zählt die ganze
 *    Ausgabe zum Bildsatz — die sichere Seite, mit Fahne.
 * ⚠️ Ohne `usageMetadata`: je Bild der Preis bei 1K (die Vorgabe, keiner
 *    unserer Aufrufe setzt imageSize), mit Fahne.
 * ⚠️ Unbekanntes Modell (GEMINI_IMAGE_MODEL): der teuerste belegte Satz.
 *
 * Wirft nie.
 */
export function kostenBild(v: {
  modell: string;
  nutzung?: BildNutzung | null;
  bilder?: number;
}): DienstKosten {
  const n = (x: unknown): number => {
    const z = Number(x);
    return Number.isFinite(z) && z > 0 ? z : 0;
  };
  const bekannt = BILD_PREISE[(v.modell ?? "").toLowerCase()];
  const teuerster = Object.values(BILD_PREISE).reduce((a, b) =>
    b.usdJeMioAusBild > a.usdJeMioAusBild ? b : a,
  );
  const p = bekannt ?? teuerster;
  const u = v.nutzung;
  const bilder = Math.max(1, Math.round(n(v.bilder) || 1));
  if (!u || n(u.promptTokenCount) + n(u.candidatesTokenCount) === 0) {
    return {
      usd: (bilder * p.tokenJeBild * p.usdJeMioAusBild) / 1_000_000,
      geschaetzt: true,
      abgerechnet: bilder,
    };
  }
  const details = Array.isArray(u.candidatesTokensDetails) ? u.candidatesTokensDetails : [];
  const bildToken = details
    .filter((d) => String(d?.modality ?? "").toUpperCase() === "IMAGE")
    .reduce((s, d) => s + n(d.tokenCount), 0);
  const aufgeteilt = details.length > 0;
  const ausBild = aufgeteilt ? bildToken : n(u.candidatesTokenCount);
  const ausText =
    (aufgeteilt ? Math.max(0, n(u.candidatesTokenCount) - bildToken) : 0) +
    n(u.thoughtsTokenCount);
  return {
    usd:
      (n(u.promptTokenCount) * p.usdJeMioEin +
        ausText * p.usdJeMioAusText +
        ausBild * p.usdJeMioAusBild) /
      1_000_000,
    geschaetzt: !bekannt || !aufgeteilt,
    abgerechnet: bilder,
  };
}
